import { SickLeaveCreateDialog } from "@/components/SickLeaveCreateDialog";
import { SickLeaveWorkerList } from "@/components/SickLeaveWorkerList";
import { WorkerTransferFlow } from "@/components/roster/WorkerTransferFlow";
import { issueUrl } from "@/lib/oprema";
import { ReturnedHandoverNotice } from "@/components/oprema/HandoverNotice";

import { PrijelaznoRazdoblje } from "@/components/PrijelaznoRazdoblje";
import { usePostavke } from "@/lib/postavke";
import { useCallback, useEffect, useState } from "react";
import {
  Link,
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { SiteWorkTabs } from "@/components/SiteWorkTabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, PageHeader, Select } from "@/components/ui/design-system";
import { SkeletonTablica } from "@/components/ui/skeleton";
import { BlokadaOverrideDialog } from "@/components/BlokadaOverrideDialog";
import {
  RosterAssignmentDialog,
  shiftDay,
} from "@/components/RosterAssignmentDialog";
import { PeriodLockNotice, usePeriodLock } from "@/components/workflow/shared";
import { useAuth } from "@/context/AuthContext";
import { useDanas } from "@/lib/dan";
import { datum } from "@/lib/format";
import {
  dohvatiGradilista,
  odobriBlokaduOverride,
  zavrsiRaspored,
  type Gradiliste,
} from "@/lib/gradilista";
import { porukaDohvata } from "@/lib/greske";
import { nazivStanja } from "@/lib/stanja";
import {
  dohvatiGradilisniSpisak,
  preuzmiGradilisniSpisakPdf,
  type GradilisniSpisak,
  type RadnikSpiska,
} from "@/lib/siteDossier";
import {
  RosterCounters,
  rosterControl,
  rosterLink,
  rosterPanel,
} from "@/pages/GradilisniPregledPage";

function blocked(worker: RadnikSpiska) {
  return worker.na_bolovanju || worker.ceka_odobrenje || worker.eligibility === "nepodoban";
}
function WorkerStatus({ worker }: { worker: RadnikSpiska }) {
  const { prijelazno } = usePostavke();
  if (worker.na_bolovanju) return <Badge ton="bad">Na bolovanju {worker.stanje_doznake === "ima" ? "✓" : "✗"}</Badge>;
  const critical = worker.uskoro.some((item) => item.dana_do <= 14);
  return (
    <Badge
      ton={
        blocked(worker) || critical
          ? "bad"
          : worker.uskoro.length
            ? "warn"
            : "ok"
      }
    >
      {worker.ceka_odobrenje && !prijelazno
        ? "Čeka iznimku"
        : worker.eligibility === "nepodoban"
          ? prijelazno
            ? "Ne bi smio raditi · prijelazno"
            : "Ne smije raditi"
          : worker.uskoro.length
            ? `Ističe za ${Math.min(...worker.uskoro.map((item) => item.dana_do))} dana`
            : "Smije raditi"}
    </Badge>
  );
}
function WorkerNeeds({ worker }: { worker: RadnikSpiska }) {
  return (
    <>
      {worker.reasons.map((reason) => (
        <p
          key={`${reason.obligation}-${reason.state}`}
          className="text-sm text-red-800"
        >
          {reason.obligation}: {nazivStanja(reason.state)}
          {reason.due_on ? ` · ${datum(reason.due_on)}` : ""}
        </p>
      ))}
      {worker.uskoro.map((item) => (
        <p
          className={`text-sm ${item.dana_do <= 14 ? "text-red-800" : "text-amber-900"}`}
          key={`${item.naziv}-${item.rok}`}
        >
          {item.naziv}: {datum(item.rok)} · {item.dana_do} dana
        </p>
      ))}
      {worker.override_active && (
        <p className="text-sm text-amber-900">
          Odobrena iznimka: {worker.override_reason}
        </p>
      )}
      {!worker.reasons.length &&
        !worker.uskoro.length &&
        !worker.override_active &&
        "—"}
    </>
  );
}

export function GradilisniSpisakPage() {
  const { can } = useAuth();
  const { gradilisteId = "" } = useParams();
  if (!can("gradilisni_spisak", "read") && can("bolovanje", "create")) return <div className="space-y-4"><PageHeader title="Gradilišni popis" meta="Prijava bolovanja za radnike na vašim gradilištima" actions={can("ozo_zahtjev", "create") ? <Link className="inline-flex min-h-11 items-center rounded border px-4 font-semibold" to={gradilisteId ? `/ozo-zahtjevi?gradiliste=${encodeURIComponent(gradilisteId)}` : "/ozo-zahtjevi"}>Treba opreme</Link> : undefined} /><SickLeaveWorkerList key={gradilisteId} siteId={gradilisteId} /></div>;
  return <FullSiteDossier />;
}

function FullSiteDossier() {
  const { can } = useAuth();
  const { prijelazno } = usePostavke();
  const today = useDanas();
  const { gradilisteId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const date = params.get("date") || today;
  const [sites, setSites] = useState<Gradiliste[] | null>(null);
  const [data, setData] = useState<GradilisniSpisak | null>(null);
  const [loaded, setLoaded] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("all");
  const [exporting, setExporting] = useState(false);
  const [adding, setAdding] = useState(params.get("dodaj") === "1");
  const [override, setOverride] = useState<RadnikSpiska | null>(null);
  const [sickWorker, setSickWorker] = useState<RadnikSpiska | null>(null);
  const [ending, setEnding] = useState<RadnikSpiska | null>(null);
  const [mobileActions, setMobileActions] = useState<RadnikSpiska | null>(null);
  const [end, setEnd] = useState(today);
  const [saving, setSaving] = useState(false);
  const lock = usePeriodLock(gradilisteId, shiftDay(end, 1), "9999-12-31");
  const allowed = can("gradilisni_spisak", "read");
  const key = `${gradilisteId}:${date}`;
  const refresh = useCallback(async () => {
    const result = await dohvatiGradilisniSpisak(gradilisteId, date);
    setData(result);
    setLoaded(`${gradilisteId}:${date}`);
    setError(null);
  }, [gradilisteId, date]);
  useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    void Promise.all([
      dohvatiGradilista(true, controller.signal),
      dohvatiGradilisniSpisak(gradilisteId, date, controller.signal),
    ])
      .then(([available, result]) => {
        if (controller.signal.aborted) return;
        setSites(available);
        setData(result);
        setLoaded(key);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted) setError(porukaDohvata(e));
      });
    return () => controller.abort();
  }, [allowed, gradilisteId, date, key, prijelazno]);
  if (!allowed) return <Navigate to="/" replace />;
  const site = sites?.find((item) => item.id === gradilisteId);
  const ready = loaded === key && data !== null;
  const workers = ready ? [...data.workers, ...data.pending_workers] : [];
  const sorted = workers
    .filter((worker) =>
      filter === "blocked"
        ? blocked(worker)
        : filter === "soon"
          ? worker.uskoro.length > 0
          : true,
    )
    .sort(
      (a, b) =>
        Number(blocked(b)) - Number(blocked(a)) ||
        (filter === "crews"
          ? (a.brigada || "Bez brigade").localeCompare(
              b.brigada || "Bez brigade",
              "hr",
            )
          : 0) ||
        a.full_name.localeCompare(b.full_name, "hr"),
    );
  function closeAdding() {
    setAdding(false);
    const next = new URLSearchParams(params);
    next.delete("dodaj");
    next.delete("radnik");
    setParams(next, { replace: true });
  }
  function startEnd(worker: RadnikSpiska) {
    setEnding(worker);
    setEnd(today);
    setMobileActions(null);
  }
  function transferAction(worker: RadnikSpiska) {
    return worker.ceka_odobrenje && !prijelazno ? null : (
      <WorkerTransferFlow
        radnikId={worker.radnik_id}
        trenutniRaspored={{
          id: worker.raspored_id,
          radnik_id: worker.radnik_id,
          radnik: worker.full_name,
          gradiliste_id: gradilisteId,
          gradiliste: site?.naziv ?? data?.site_name ?? null,
          vrijedi_od: worker.vrijedi_od,
          vrijedi_do: null,
          brigada_id: worker.brigada_id,
        }}
        zadanoGradilisteId={gradilisteId}
        onDone={async () => {
          setMobileActions(null);
          await refresh();
        }}
      />
    );
  }
  function actions(worker: RadnikSpiska) {
    return (
      <div className="flex flex-wrap gap-2">
        {transferAction(worker)}
        {can("bolovanje", "create") && !worker.na_bolovanju && <Button className="min-h-11" variant="outline" onClick={() => { setMobileActions(null); setSickWorker(worker); }}>Bolovanje</Button>}
        {can("oprema", "create") && <Link className="inline-flex min-h-11 items-center rounded border px-4 underline" to={issueUrl(worker.radnik_id, gradilisteId)} state={{ from: `/gradilisni-spisak/${gradilisteId}?${params}` }}>Izdaj opremu</Link>}
        {!prijelazno &&
          worker.ceka_odobrenje &&
          can("djelatnik", "blokada_override") && (
            <Button
              className="min-h-11"
              variant="outline"
              onClick={() => {
                setOverride(worker);
                setMobileActions(null);
              }}
            >
              Odobri iznimku
            </Button>
          )}
        {can("raspored", "update") && (
          <Button
            className="min-h-11"
            variant="outline"
            onClick={() => startEnd(worker)}
          >
            Makni
          </Button>
        )}
      </div>
    );
  }
  async function finish() {
    if (!ending || lock.blocked || end < ending.vrijedi_od || saving) return;
    setSaving(true);
    try {
      await zavrsiRaspored(ending.raspored_id, {
        radnik_id: ending.radnik_id,
        gradiliste_id: gradilisteId,
        vrijedi_od: ending.vrijedi_od,
        vrijedi_do: end,
      });
      setEnding(null);
      await refresh();
    } catch (e: unknown) {
      setError(porukaDohvata(e));
    } finally {
      setSaving(false);
    }
  }
  async function exportPdf() {
    setExporting(true);
    try {
      await preuzmiGradilisniSpisakPdf(gradilisteId, date);
    } catch (e: unknown) {
      setError(porukaDohvata(e));
    } finally {
      setExporting(false);
    }
  }
  return (
    <div className="flex min-w-0 flex-col gap-5">
      <Link className={rosterLink} to={`/gradilisni-spisak?date=${date}`}>
        ← Sva gradilišta
      </Link>
      <PrijelaznoRazdoblje />
      {params.get("kartica") !== "oprema" && <ReturnedHandoverNotice onUndone={() => void refresh()} />}
      <PageHeader
        title={site?.naziv || data?.site_name || "Gradilišni popis"}
        actions={can("ozo_zahtjev", "create") && gradilisteId ? <Link className="inline-flex min-h-11 items-center rounded border px-4 font-semibold" to={`/ozo-zahtjevi?gradiliste=${encodeURIComponent(gradilisteId)}`}>Treba opreme</Link> : undefined}
        meta={
          site
            ? `${site.adresa || "Adresa nije unesena"} · voditelj ${site.voditelj || "—"} · zamjenik ${site.zamjenik || "—"}`
            : "Dnevni popis radnika"
        }
      />
      <div className="grid gap-3 sm:grid-cols-2">
        <label>
          Gradilište
          <Select
            aria-label="Gradilište"
            className={rosterControl}
            value={gradilisteId}
            onChange={(e) => {
              setSickWorker(null);
              setAdding(false);
              setEnding(null);
              setOverride(null);
              setMobileActions(null);
              const next = new URLSearchParams({ date });
              const card = params.get("kartica");
              if (card) next.set("kartica", card);
              navigate(`/gradilisni-spisak/${e.target.value}?${next}`);
            }}
          >
            {sites?.map((item) => (
              <option key={item.id} value={item.id}>
                {item.naziv}
              </option>
            ))}
          </Select>
        </label>
        <label>
          Dan
          <input
            aria-label="Datum popisa"
            className={rosterControl}
            type="date"
            value={date}
            onChange={(e) => {
              if (e.target.value) {
                const next = new URLSearchParams(params);
                next.set("date", e.target.value);
                setParams(next);
              }
            }}
          />
        </label>
      </div>
      <div className="flex flex-wrap gap-3">
        {can("raspored", "create") && (
          <Button className="min-h-11" onClick={() => setAdding(true)}>
            Dodaj radnike
          </Button>
        )}
        {can("sati", "create") && (
          <Link
            className={`${rosterLink} rounded-lg border px-3`}
            to={`/sati?gradiliste_id=${gradilisteId}&date=${date}`}
          >
            Upiši sate za danas
          </Link>
        )}
        {can("gradilisni_spisak", "export") && (
          <Button
            className="min-h-11"
            variant="outline"
            disabled={!ready || exporting || !data?.workers.length}
            onClick={() => void exportPdf()}
          >
            {exporting ? "Pripremam PDF…" : "PDF za inspekciju"}
          </Button>
        )}
      </div>
      {error && (
        <p role="alert" className="text-red-800">
          {error}
        </p>
      )}
      {!ready ? (
        !error && <SkeletonTablica redaka={5} />
      ) : (
        <SiteWorkTabs
          siteId={gradilisteId}
          date={date}
          peopleCount={workers.length}
          onChanged={refresh}
          transitional={prijelazno}
        >
          <RosterCounters
            values={[
              { label: "Efektivnih radnika", count: data.workers.length },
              {
                label: prijelazno ? "Bez potrebnih uvjeta" : "Ne smiju raditi",
                count: workers.filter(blocked).length,
                tone: "bad",
              },
              {
                label: "Uskoro ističe",
                count: workers.filter((worker) => worker.uskoro.length > 0)
                  .length,
                tone: "warn",
              },
              { label: "Čekaju iznimku", count: data.pending_workers.length },
            ]}
          />
          <div
            className="flex flex-wrap gap-2"
            role="group"
            aria-label="Filtri radnika"
          >
            {[
              ["all", "Svi"],
              [
                "blocked",
                prijelazno ? "Bez potrebnih uvjeta" : "Ne smiju raditi",
              ],
              ["soon", "Uskoro ističe"],
              ["crews", "Po brigadama"],
            ].map(([value, label]) => (
              <Button
                className="min-h-11"
                key={value}
                variant={filter === value ? "default" : "outline"}
                aria-pressed={filter === value}
                onClick={() => setFilter(value!)}
              >
                {label}
              </Button>
            ))}
          </div>
          {filter === "crews" && (
            <p className="text-sm">
              Radnici su poredani po brigadama; oni koji ne smiju raditi ostaju
              na vrhu.
            </p>
          )}
          <section className={rosterPanel}>
            <h2 className="border-b p-4 text-lg font-semibold">
              Tko je na gradilištu · {datum(date)}
            </h2>
            {sorted.length === 0 ? (
              <p className="p-4">Nema radnika za odabrani prikaz.</p>
            ) : (
              <>
                <table className="hidden w-full text-left lg:table">
                  <thead>
                    <tr>
                      {[
                        "Radnik",
                        "Radno mjesto",
                        "Brigada",
                        "Od",
                        "Stanje",
                        "Što treba",
                        "Akcije",
                      ].map((label) => (
                        <th className="p-3 text-xs uppercase" key={label}>
                          {label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sorted.map((worker) => (
                      <tr
                        className={`border-t align-top ${blocked(worker) ? "bg-red-50" : ""}`}
                        key={worker.raspored_id}
                        data-testid={`worker-row-${worker.radnik_id}`}
                      >
                        <td className="p-3">
                          <Link
                            className={rosterLink}
                            to={`/zaposlenici/${worker.radnik_id}`}
                          >
                            {worker.full_name}
                          </Link>
                        </td>
                        <td className="p-3">{worker.job_position || "—"}</td>
                        <td className="p-3">
                          {worker.brigada || "Bez brigade"}
                        </td>
                        <td className="p-3 whitespace-nowrap">
                          {datum(worker.vrijedi_od)}
                        </td>
                        <td className="p-3">
                          <WorkerStatus worker={worker} />
                        </td>
                        <td className="max-w-64 p-3">
                          <WorkerNeeds worker={worker} />
                        </td>
                        <td className="p-3">{actions(worker)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="flex flex-col gap-3 p-3 lg:hidden">
                  {sorted.map((worker) => (
                    <article
                      key={worker.raspored_id}
                      data-testid={`worker-card-${worker.radnik_id}`}
                      className={`${rosterPanel} p-4 ${blocked(worker) ? "border-red-300 bg-red-50" : ""}`}
                    >
                      <button
                        type="button"
                        className="min-h-11 w-full text-left"
                        onClick={() => setMobileActions(worker)}
                      >
                        <span className="mb-2 block font-semibold">
                          {worker.full_name}
                        </span>
                        <span className="mb-2 block text-sm">
                          {worker.job_position || "—"} ·{" "}
                          {worker.brigada || "Bez brigade"} · od{" "}
                          {datum(worker.vrijedi_od)}
                        </span>
                        <WorkerStatus worker={worker} />
                        <span className="mt-3 block">
                          <WorkerNeeds worker={worker} />
                        </span>
                        <span className="mt-3 block text-sm underline">
                          Otvori akcije
                        </span>
                      </button>
                      <div className="mt-3">{transferAction(worker)}</div>
                    </article>
                  ))}
                </div>
              </>
            )}
          </section>
        </SiteWorkTabs>
      )}
      {sickWorker && <SickLeaveCreateDialog workerId={sickWorker.radnik_id} workerName={sickWorker.full_name} siteId={gradilisteId} onClose={() => setSickWorker(null)} onChanged={refresh} />}
      {adding && can("raspored", "create") && (
        <RosterAssignmentDialog
          key={gradilisteId}
          siteId={gradilisteId}
          siteName={site?.naziv || data?.site_name || "Gradilište"}
          initialWorker={params.get("radnik")}
          onClose={closeAdding}
          onSaved={refresh}
        />
      )}
      {mobileActions && (
        <Dialog
          title={mobileActions.full_name}
          onClose={() => setMobileActions(null)}
        >
          <WorkerStatus worker={mobileActions} />
          <div className="my-3">
            <WorkerNeeds worker={mobileActions} />
          </div>
          <Link
            className={rosterLink}
            to={`/zaposlenici/${mobileActions.radnik_id}`}
          >
            Otvori karton
          </Link>
          {actions(mobileActions)}
        </Dialog>
      )}
      {override && (
        <BlokadaOverrideDialog
          raspored={{
            id: override.raspored_id,
            radnik_id: override.radnik_id,
            radnik: override.full_name,
            gradiliste_id: gradilisteId,
            gradiliste: site?.naziv || data?.site_name || null,
            vrijedi_od: override.vrijedi_od,
            vrijedi_do: null,
            blocking_reasons: override.reasons.map(
              (reason) => `${reason.obligation}: ${nazivStanja(reason.state)}`,
            ),
          }}
          onOdustani={() => setOverride(null)}
          onPotvrdi={async (reason) => {
            await odobriBlokaduOverride(override.raspored_id, reason);
            setOverride(null);
            await refresh();
          }}
        />
      )}
      {ending && (
        <Dialog
          title={`Makni s gradilišta — ${ending.full_name}`}
          onClose={() => {
            if (!saving) setEnding(null);
          }}
        >
          <p className="mb-3">
            Upisuje se zadnji dan rada ovdje. Radnik ostaje u povijesti ovog
            gradilišta.
          </p>
          <label>
            Zadnji dan
            <input
              className={rosterControl}
              type="date"
              min={ending.vrijedi_od}
              value={end}
              onChange={(e) => {
                if (e.target.value) setEnd(e.target.value);
              }}
            />
          </label>
          <PeriodLockNotice state={lock.state} />
          {error && <p role="alert">{error}</p>}
          <Button
            className="mt-4 min-h-11"
            disabled={saving || lock.blocked || end < ending.vrijedi_od}
            onClick={() => void finish()}
          >
            {saving ? "Spremanje…" : "Spremi zadnji dan"}
          </Button>
        </Dialog>
      )}
    </div>
  );
}
